import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/auth.json' with { type: 'json' };
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { Brand } from "../shell/Brand.js";
import { ModeToggle } from "../shell/TopBar.js";
import { Button } from "../components/ui/Button.js";
import { Callout } from "../components/ui/Callout.js";
import { Field, Input } from "../components/ui/Form.js";
import { FullPageSpinner } from "../components/ui/FullPage.js";
import { useSession } from "./SessionProvider.js";
import {
  activationCoordinator,
  createActivationClient,
  type ActivationAuthClient,
  type ActivationCoordinator,
  type ActivationInvalidReason,
  type ActivationResult,
} from "./activation.js";

const invalidCopy: Record<ActivationInvalidReason, string> = {
  missing: copy.ActivationPage.thisActivationLinkIsMissingOrIncomplete,
  malformed: copy.ActivationPage.thisActivationLinkIsNotValid,
  expired: copy.ActivationPage.thisActivationLinkHasExpiredOrHas,
  blocked: copy.ActivationPage.thisAccountIsNotAvailableForActivation,
  subject: copy.ActivationPage.thisActivationLinkDoesNotMatchThe,
  unavailable: copy.ActivationPage.accountActivationIsTemporarilyUnavailablePleaseTry,
};

interface ActivationPageProps {
  coordinator?: ActivationCoordinator;
  createClient?: typeof createActivationClient;
  fetchImpl?: typeof fetch;
}

type PageState = { kind: "loading" } | { kind: "confirmation" } | ActivationResult;

export function ActivationPage(props: ActivationPageProps = {}) {
  const location = useLocation();
  // A second activation URL reached in the same SPA is a distinct attempt.
  // Main-session identity remounts keep the same route key and reuse the
  // coordinator's already-scrubbed isolated attempt.
  return <ActivationAttemptPage key={`${location.key}:${location.search}:${location.hash}`} {...props} />;
}

function ActivationAttemptPage({
  coordinator = activationCoordinator,
  createClient = createActivationClient,
  fetchImpl = fetch,
}: ActivationPageProps = {}) {
  const { config, signOut } = useSession();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ kind: "confirmation" });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldError, setFieldError] = useState<"password" | "confirm" | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const clientRef = useRef<ActivationAuthClient | null>(null);
  const active = useRef(false);
  const continuing = useRef(false);

  coordinator.capture(new URL(window.location.href), () => {
    const clean = window.location.pathname;
    window.history.replaceState(window.history.state, document.title, clean);
  });

  useEffect(() => {
    active.current = true;
    const release = coordinator.retain();
    const started = coordinator.started();
    if (started) {
      clientRef.current = coordinator.acquireClient(() => createClient(config));
      setState({ kind: "loading" });
      void started.then(result => { if (active.current) setState(result); });
    } else {
      setState(coordinator.landingState());
    }
    return () => { active.current = false; release(); };
  }, [config, coordinator, createClient, fetchImpl]);

  async function continueSetup() {
    if (continuing.current || state.kind !== "confirmation") return;
    continuing.current = true;
    setState({ kind: "loading" });
    const client = coordinator.acquireClient(() => createClient(config));
    clientRef.current = client;
    if (!client) { setState({ kind: "invalid", reason: "unavailable" }); return; }
    const result = await coordinator.initialize(client, fetchImpl);
    if (active.current) setState(result);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting.current || state.kind !== "ready" || !clientRef.current) return;
    setFieldError(null);
    setSubmitError(null);
    if (password.length < 8) {
      setFieldError("password");
      return;
    }
    if (password !== confirmPassword) {
      setFieldError("confirm");
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      try {
        const updated = await clientRef.current.auth.updateUser({ password });
        if (updated.error || !updated.data.user?.email || updated.data.user.is_anonymous || !updated.data.user.email_confirmed_at || updated.data.user.email.toLowerCase() !== state.identity.email.toLowerCase()) {
          setSubmitError(copy.ActivationPage.yourPasswordCouldNotBeSetChoose);
          return;
        }
      } catch {
        setSubmitError(copy.ActivationPage.yourPasswordCouldNotBeSetCheck);
        return;
      }
      coordinator.clear();
      try { await signOut(copy.ActivationPage.yourPasswordIsReadySignInTo); } catch { /* local main-session state is cleared first */ }
      navigate("/login", { replace: true });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  if (state.kind === "loading") return <FullPageSpinner label={copy.ActivationPage.checkingYourActivationLink} />;

  return (
    <div className="fullpage">
      <div className="fullpage__mode"><ModeToggle /></div>
      <div className="fullpage__card">
        <div className="fullpage__hero">
          <Brand size="lg" to={null} />
          <h1 className="fullpage__title">{copy.ActivationPage.activateStaffAccount}</h1>
          <p className="fullpage__sub">{copy.ActivationPage.chooseYourSignInPassword}</p>
        </div>
        {state.kind === "invalid" ? (
          <Callout tone="danger" title={copy.ActivationPage.activationLinkUnavailable}>
            {invalidCopy[state.reason]}{copy.ActivationPage.askAnAdministratorToSendYouA}</Callout>
        ) : state.kind === "confirmation" ? (
          <div className="stack">
            <p>{copy.ActivationPage.continueToVerifyYourInvitationAndChoose}</p>
            <Button type="button" variant="primary" onClick={() => void continueSetup()}>{copy.ActivationPage.continueToPasswordSetup}</Button>
          </div>
        ) : (
          <form className="login-form" onSubmit={(event) => void submit(event)} noValidate>
            <Callout tone="info" title={state.identity.displayName ? formatCopy(copy.ActivationPage.welcome, { name: state.identity.displayName }) : copy.ActivationPage.staffAccountFound}>{copy.ActivationPage.thisLinkIsFor}<strong>{state.identity.email}</strong>.
            </Callout>
            <Field label={copy.ActivationPage.accountEmail} help={copy.ActivationPage.thisSetupLinkIsBoundToThis}>
              {control => <Input {...control} type="email" autoComplete="username" value={state.identity.email} readOnly />}
            </Field>
            <Field label={copy.ActivationPage.password} help={copy.ActivationPage.useAtLeast8Characters} error={fieldError === "password" ? copy.ActivationPage.chooseAPasswordWithAtLeast8 : null}>
              {control => <Input {...control} type="password" autoComplete="new-password" value={password} onChange={event => setPassword(event.target.value)} disabled={busy} />}
            </Field>
            <Field label={copy.ActivationPage.confirmPassword} error={fieldError === "confirm" ? copy.ActivationPage.passwordsDoNotMatch : null}>
              {control => <Input {...control} type="password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} disabled={busy} />}
            </Field>
            {submitError ? <Callout tone="danger">{submitError}</Callout> : null}
            <Button type="submit" variant="primary" loading={busy}>{copy.ActivationPage.activateAccount}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
